import { Image, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';

type VariantOption = {
  id?: string; // Optional ID for existing variants
  attributes: Record<string, string>;
  mrp: number;
  discountPercent: number;
  stock: number;
  sku: string;
  existingImages: string[]; // Existing URLs from backend
  images: File[];           // New uploads
  discountedPrice: number;
};

type ProductVariantConfig = {
  hasVariants: boolean;
  attributes: string[];
  variantOptions: Record<string, string[]>;
  variants: VariantOption[];
};

interface ProductVariantManagerProps {
  variantConfig: ProductVariantConfig;
  onVariantConfigChange: (config: ProductVariantConfig) => void;
  toast: {
    showToast: (message: string, type: "success" | "error" | "info") => void;
  };
}

type VariantOptionDef = {
  id: string;
  name: string;
  values: string[];
  valueInput: string;
};

const defaultOptionNames = ["Color", "Size"];

function getCombinations(defs: VariantOptionDef[]): Record<string, string>[] {
  if (defs.length === 0 || defs.some(d => d.values.length === 0 || !d.name)) return [];
  const combine = (idx: number, acc: Record<string, string>): Record<string, string>[] => {
    if (idx === defs.length) return [acc];
    const def = defs[idx];
    return def.values.flatMap(val => combine(idx + 1, { ...acc, [def.name]: val }));
  };
  return combine(0, {});
}

const ProductVariantManager: React.FC<ProductVariantManagerProps> = ({
  variantConfig,
  onVariantConfigChange,
  toast
}) => {
  const [optionDefs, setOptionDefs] = useState<VariantOptionDef[]>(() => {
    if (variantConfig.attributes.length > 0) {
      return variantConfig.attributes.map((name) => ({
        id: uuidv4(),
        name,
        values: variantConfig.variantOptions[name] || [],
        valueInput: ""
      }));
    }
    return [];
  });

  // State for variant details
  const [variantDetails, setVariantDetails] = useState<Record<string, {
    id?: string; // Variant ID for existing variants
    mrp: number | undefined;
    discountPercent: number | undefined;
    stock: number | undefined;
    existingImages: string[]; // Existing URLs from backend
    images: File[];           // New uploads
    discountedPrice: number;
  }>>(() => {
    // Initialize from existing variantConfig if available
    if (variantConfig.hasVariants && variantConfig.variants.length > 0) {
      const initialDetails: Record<string, any> = {};
      variantConfig.variants.forEach(variant => {
        const key = Object.values(variant.attributes).join("||");
        initialDetails[key] = {
          id: variant.id, // Preserve existing variant ID
          mrp: variant.mrp,
          discountPercent: variant.discountPercent,
          stock: variant.stock,
          existingImages: variant.existingImages || [],
          images: variant.images || [],
          discountedPrice: variant.discountedPrice
        };
      });
      console.log("Initialized variantDetails from existing config:", initialDetails);
      return initialDetails;
    }
    return {};
  });

  // Sync optionDefs to parent config
  const syncToConfig = (defs: VariantOptionDef[]) => {
    const attributes = defs.map((d) => d.name);
    const variantOptions: Record<string, string[]> = {};
    defs.forEach((d) => {
      variantOptions[d.name] = d.values;
    });
    
    // Generate combinations and sync variant details
    const combos = getCombinations(defs);
    console.log("combos:", combos);
    const variants: VariantOption[] = combos.map((combo, index) => {
      const key = defs.map(opt => combo[opt.name]).join("||");
      const details = variantDetails[key] || { mrp: undefined, discountPercent: undefined, stock: undefined, existingImages: [], images: [], discountedPrice: 0 };
      
      console.log(`Variant ${index} - key: ${key}, details:`, details);
      
      // Generate SKU
      const skuParts = Object.entries(combo).map(([key, value]) => 
        `${key.toUpperCase().substring(0, 3)}-${value.toUpperCase()}`
      );
      const sku = `PROD-${skuParts.join('-')}`;
      
      // Calculate discounted price with defaults
      const mrp = details.mrp !== undefined ? details.mrp : 0;
      const discountPercent = details.discountPercent !== undefined ? details.discountPercent : 0;
      const discountedPrice = mrp - (mrp * discountPercent / 100);
      
      // Preserve existing ID or generate new one
      const variantId = details.id || `variant_${Date.now()}_${index}`;
      
      return {
        id: variantId, // Include variant ID
        attributes: combo,
        mrp: mrp,
        discountPercent: discountPercent,
        stock: details.stock !== undefined ? details.stock : 0,
        sku,
        existingImages: details.existingImages,
        images: details.images,
        discountedPrice: discountedPrice
      };
    });
    
    console.log("Generated variants:", variants);
    
    // Set hasVariants based on whether we have valid variants
    const hasVariants = attributes.length > 0 && attributes.every(attr => variantOptions[attr] && variantOptions[attr].length > 0) && variants.length > 0;
    
    onVariantConfigChange({
      ...variantConfig,
      hasVariants,
      attributes,
      variantOptions,
      variants
    });
  };

  // Handle variant detail change
  const handleVariantDetailChange = (key: string, field: string, value: any) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // Sync data when optionDefs change
  useEffect(() => {
    if (optionDefs.length > 0 && optionDefs.some(d => d.name && d.values.length > 0)) {
      syncToConfig(optionDefs);
    }
  }, [optionDefs]);

  // Sync data when variantDetails change
  useEffect(() => {
    if (optionDefs.length > 0 && optionDefs.some(d => d.name && d.values.length > 0)) {
      console.log("=== PRODUCTVARIANTMANAGER DEBUG ===");
      console.log("optionDefs:", optionDefs);
      console.log("variantDetails:", variantDetails);
      syncToConfig(optionDefs);
    }
  }, [variantDetails, optionDefs]);

  // Add new option
  const handleAddOption = () => {
    setOptionDefs((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name: "",
        values: [],
        valueInput: ""
      }
    ]);
  };

  // Remove option
  const handleRemoveOption = (id: string) => {
    const newDefs = optionDefs.filter((o) => o.id !== id);
    setOptionDefs(newDefs);
    syncToConfig(newDefs);
  };

  // Edit option name
  const handleOptionNameChange = (id: string, name: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) => (o.id === id ? { ...o, name, values: [] } : o));
      return updated;
    });
  };

  // Add value to option
  const handleAddValue = (id: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) => {
        if (o.id !== id) return o;
        const val = o.valueInput.trim();
        if (!val || o.values.includes(val)) return o;
        return { ...o, values: [...o.values, val], valueInput: "" };
      });
      return updated;
    });
  };

  // Remove value from option
  const handleRemoveValue = (id: string, value: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) =>
        o.id === id ? { ...o, values: o.values.filter((v) => v !== value) } : o
      );
      return updated;
    });
  };

  // Handle value input change
  const handleValueInputChange = (id: string, value: string) => {
    setOptionDefs((prev) =>
      prev.map((o) => (o.id === id ? { ...o, valueInput: value } : o))
    );
  };

  // Handle image change for a variant
  const handleVariantImageChange = (key: string, files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        images: [...(prev[key]?.images || []), ...arr]
      }
    }));
  };

  // Remove image for a variant
  const handleRemoveVariantImage = (key: string, idx: number) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        images: prev[key]?.images.filter((_, i) => i !== idx) || []
      }
    }));
  };

  // Remove existing image for a variant
  const handleRemoveExistingVariantImage = (key: string, idx: number) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        existingImages: prev[key]?.existingImages.filter((_, i) => i !== idx) || []
      }
    }));
  };

  // UI rendering
  return (
    <div className="space-y-6">
      {/* No options yet */}
      {optionDefs.length === 0 && (
        <button
          type="button"
          onClick={handleAddOption}
          className="flex items-center gap-2 text-blue-600 hover:underline font-medium px-2 py-2 rounded transition"
        >
          <span className="text-xl">+</span> Add options like size or color
        </button>
      )}

      {/* Option cards */}
      <div className="space-y-2">
        {optionDefs.map((option, idx) => (
          <div key={option.id} className="relative group bg-white border border-gray-200 p-4 flex flex-col gap-2">
            {/* Delete icon (show on hover) */}
            <button
              type="button"
              onClick={() => handleRemoveOption(option.id)}
              className="absolute right-3 top-3 text-gray-300 group-hover:text-red-500 transition-opacity opacity-0 group-hover:opacity-100"
              aria-label="Delete option"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Option name */}
            <div className="flex-1 min-w-[120px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Option name</label>
              <select
                value={option.name || ''}
                onChange={e => handleOptionNameChange(option.id, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>Select option...</option>
                {defaultOptionNames.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Option values (separate row) */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Option values</label>
              <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 px-2 py-1 min-h-[44px]">
                {option.values.map((val) => (
                  <span key={val} className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 text-xs border border-blue-200">
                    {val}
                    <button
                      type="button"
                      className="ml-1 text-blue-600 hover:text-red-600"
                      onClick={() => handleRemoveValue(option.id, val)}
                      aria-label="Remove value"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={option.valueInput}
                  onChange={e => handleValueInputChange(option.id, e.target.value)}
                  placeholder="Type and press Enter"
                  className="flex-1 min-w-[80px] px-2 py-1 border-none bg-transparent focus:ring-0 text-sm"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddValue(option.id);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddValue(option.id)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add another option */}
      {optionDefs.length > 0 && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={handleAddOption}
            className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 border border-blue-100 font-medium px-4 py-2 transition"
          >
            <span className="text-xl">+</span> Add another option
          </button>
        </div>
      )}

      {/* Variant combinations table */}
      {(() => {
        const combos = getCombinations(optionDefs);
        if (combos.length === 0) return null;
        return (
          <div className="mt-8">
            <div className="bg-white border border-gray-200 p-0">
              <h4 className="font-semibold text-gray-800 mb-3 px-4 pt-4">Variant Combinations</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs sm:text-sm bg-white">
                  <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                    <tr>
                      {optionDefs.map(opt => (
                        <th key={opt.name} className="px-3 py-2 border-b text-left font-medium text-gray-600">{opt.name}</th>
                      ))}
                      <th className="px-3 py-2 border-b text-left font-medium text-gray-600" title="Set the price for this variant">Price</th>
                      <th className="px-3 py-2 border-b text-left font-medium text-gray-600" title="Discount percentage for this variant">Discount (%)</th>
                      <th className="px-3 py-2 border-b text-left font-medium text-gray-600" title="Inventory/stock for this variant">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combos.map((combo, idx) => {
                      const key = optionDefs.map(opt => combo[opt.name]).join("||");
                      const details = variantDetails[key] || { mrp: undefined, discountPercent: undefined, stock: undefined, existingImages: [], images: [], discountedPrice: 0 };
                      return (
                        <React.Fragment key={key}>
                          <tr className="border-b border-gray-200 hover:bg-blue-50 transition">
                            {optionDefs.map(opt => (
                              <td key={opt.name} className="px-3 py-2 align-top font-medium text-gray-800">{combo[opt.name]}</td>
                            ))}
                            <td className="px-3 py-2 align-top">
                              <input
                                type="number"
                                value={details.mrp !== undefined ? details.mrp : ""}
                                min={0}
                                onChange={e => {
                                  const value = e.target.value;
                                  if (value === "") {
                                    handleVariantDetailChange(key, 'mrp', undefined);
                                  } else {
                                    const numValue = parseFloat(value);
                                    handleVariantDetailChange(key, 'mrp', isNaN(numValue) ? undefined : numValue);
                                  }
                                }}
                                className="w-20 px-2 py-1 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="number"
                                value={details.discountPercent !== undefined ? details.discountPercent : ""}
                                min={0}
                                max={100}
                                onChange={e => {
                                  const value = e.target.value;
                                  if (value === "") {
                                    handleVariantDetailChange(key, 'discountPercent', undefined);
                                  } else {
                                    const numValue = parseFloat(value);
                                    handleVariantDetailChange(key, 'discountPercent', isNaN(numValue) ? undefined : numValue);
                                  }
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="number"
                                value={details.stock !== undefined ? details.stock : ""}
                                min={0}
                                onChange={e => {
                                  const value = e.target.value;
                                  if (value === "") {
                                    handleVariantDetailChange(key, 'stock', undefined);
                                  } else {
                                    const numValue = parseInt(value);
                                    handleVariantDetailChange(key, 'stock', isNaN(numValue) ? undefined : numValue);
                                  }
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                          </tr>
                          {/* Image upload row */}
                          <tr className="border-b border-gray-200">
                            <td colSpan={optionDefs.length + 3} className="bg-gray-50 px-3 pb-4 pt-0 border-t border-gray-200">
                              <div className="mt-2">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Variant Images</label>
                                <div
                                  className="border-2 border-dashed border-gray-300 p-4 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition bg-white mb-2"
                                  onClick={() => document.getElementById(`variant-image-input-${key}`)?.click()}
                                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                  onDrop={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const files = Array.from(e.dataTransfer.files);
                                    const dt = { target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                                    handleVariantImageChange(key, dt.target.files as FileList);
                                  }}
                                  tabIndex={0}
                                  aria-label="Upload variant images"
                                >
                                  <Image className="w-8 h-8 text-gray-400 mb-1" />
                                  <span className="text-gray-500 text-xs">Drag & drop images here, or <span className="text-blue-600 underline">click to upload</span></span>
                                  <input
                                    id={`variant-image-input-${key}`}
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    className="hidden"
                                    onChange={e => handleVariantImageChange(key, e.target.files)}
                                  />
                                </div>
                                {details.images && details.images.length > 0 && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                                    {details.images.map((img, i) => (
                                      <div key={i} className="relative group overflow-hidden border border-gray-200 bg-white">
                                        <img
                                          src={URL.createObjectURL(img)}
                                          alt=""
                                          className="object-cover w-full h-20 sm:h-24 transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <button
                                          type="button"
                                          className="absolute top-1 right-1 text-white bg-red-500 hover:bg-red-600 p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                                          onClick={e => { e.stopPropagation(); handleRemoveVariantImage(key, i); }}
                                          aria-label="Remove image"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {details.existingImages && details.existingImages.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Existing Images:</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                      {details.existingImages.map((imgUrl, i) => (
                                        <div key={`existing-${i}`} className="relative group overflow-hidden border border-gray-200 bg-white">
                                          <img
                                            src={imgUrl}
                                            alt=""
                                            className="object-cover w-full h-20 sm:h-24 transition-transform duration-300 group-hover:scale-105"
                                          />
                                          <button
                                            type="button"
                                            className="absolute top-1 right-1 text-white bg-red-500 hover:bg-red-600 p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                                            onClick={e => { 
                                              e.stopPropagation(); 
                                              handleRemoveExistingVariantImage(key, i); 
                                            }}
                                            aria-label="Remove existing image"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProductVariantManager; 